/**
 * PLUGIN-KERNEL — mounts the app's composition and re-mounts it whenever the
 * active account changes.
 */
import { createMemo, onCleanup, type ParentComponent } from "solid-js";
import { Kernel, KernelContext } from "@fleet/kernel";
import { appComposition, type AppContribution, type HostServices } from "@/composition";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";

export const KernelProvider: ParentComponent = (props) => {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { t } = useI18n();

  const host: HostServices = {
    db: () => db(),
    drizzle: () => drizzle(),
    userId: () => session.userId() || undefined,
    onHistoryError: () => {},
    translate: t,
  };

  const isTauri = typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
  const target = isTauri ? "tauri" : "web";
  const kernel = new Kernel<AppContribution>();

  const activeAccountId = () => session.userId() || null;

  const mounted = createMemo(() => {
    const accountId = activeAccountId();
    const app = kernel.mount(appComposition(host, target), { accountId });
    onCleanup(() => app.unmount());
    return app;
  });

  return <KernelContext.Provider value={mounted}>{props.children}</KernelContext.Provider>;
};
